import { HugeiconsIcon } from "@hugeicons/react";
import { Certificate01Icon } from "@hugeicons-pro/core-solid-rounded";
import { Download04Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { CapturedConsent, SelectedLender } from "../types";
import { formatTimestamp } from "../utils";
import { LenderLogo } from "shared/app/LenderLogo";

type LenderGroup = {
  id: string;
  name: string;
  consents: CapturedConsent[];
};

/** Short, staff-facing summary keyed by consent type. */
const CONSENT_DESCRIPTION: Record<string, string> = {
  credit_authorization:
    "Verbal credit-check authorization, captured with identity, timestamp, and IP.",
  electronic_delivery: "Agreed to receive loan documents electronically.",
  information_sharing:
    "Authorized sharing information with the selected lender."
};

const groupByLender = (consents: CapturedConsent[]): LenderGroup[] => {
  const order: string[] = [];
  const map: Record<string, LenderGroup> = {};

  for (const consent of consents) {
    if (!map[consent.targetLenderId]) {
      map[consent.targetLenderId] = {
        id: consent.targetLenderId,
        name: consent.targetLenderName,
        consents: []
      };
      order.push(consent.targetLenderId);
    }
    map[consent.targetLenderId].consents.push(consent);
  }

  return order.map((id) => map[id]);
};

const ConsentCard = ({
  consent,
  logoUrl
}: {
  consent: CapturedConsent;
  logoUrl: string | null;
}) => (
  <div className="flex flex-col gap-3 rounded-lg border border-alpha-100 bg-content-1000-inverted p-4">
    {/* Logo left, status badge right */}
    <div className="flex items-start justify-between gap-2">
      <LenderLogo
        src={logoUrl}
        alt={consent.targetLenderName}
        className="size-9 rounded-md object-contain"
        fallback={
          <div className="flex size-9 items-center justify-center rounded-md bg-alpha-40">
            <HugeiconsIcon
              icon={Certificate01Icon}
              size={18}
              className="text-content-300"
            />
          </div>
        }
      />
      <Badge
        variant={consent.accepted ? BadgeVariant.SUCCESS : BadgeVariant.BASE}
        size={BadgeSize.SM}
      >
        {consent.accepted ? "Confirmed" : "Pending"}
      </Badge>
    </div>

    <div>
      <p className="text-sm font-bold text-content-1000">{consent.title}</p>
      <p className="mt-1 text-xs font-medium text-content-300">
        {CONSENT_DESCRIPTION[consent.consentType] ??
          "Reviewed and accepted by the applicant."}
      </p>
    </div>

    {/* Footer: disclosure link + capture time, stacked so neither wraps awkwardly */}
    <div className="mt-auto space-y-2 border-t border-alpha-40 pt-3">
      <a
        href={consent.fileUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
      >
        <HugeiconsIcon icon={Download04Icon} size={14} />
        Download full disclosure
      </a>
      <Show if={consent.accepted}>
        <p className="text-xs font-medium text-content-300">
          Accepted {formatTimestamp(consent.acceptedAt)}
        </p>
      </Show>
    </div>
  </div>
);

export const ConsentsList = ({
  consents,
  lenders
}: {
  consents: CapturedConsent[];
  lenders: SelectedLender[];
}) => {
  const groups = groupByLender(consents);
  const logoById = new Map(
    lenders.map((lender) => [lender.id, lender.logoUrl])
  );

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.id} className="space-y-3">
          <h4 className="text-sm font-bold text-content-1000">{group.name}</h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {group.consents.map((consent) => (
              <ConsentCard
                key={consent.id}
                consent={consent}
                logoUrl={logoById.get(group.id) ?? null}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
